import { AlertCircle, Copy, Download, Edit, Info, Plus, Server, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import { useCsvExport } from '../../hooks/useCsvExport';
import { useRoles } from '../../hooks/useRoles';
import { getApiUrl } from '../../utils/apiUrl';
import ConfirmDialog from '../common/ConfirmDialog';
import LoadingSpinner from '../common/LoadingSpinner';
import SecureIframe from '../common/SecureIframe';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { DataTable } from '../ui/data-table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Switch } from '../ui/switch';

const RoleList = () => {
  const {
    roles,
    loading,
    error,
    operationInProgress,
    fetchRoles,
    handleDelete,
    handleToggleActive,
    handleToggleMcp,
    handleEdit,
    handleAdd,
    prepareExportData,
  } = useRoles();

  const { confirmState, openConfirm, closeConfirm, handleConfirm } = useConfirmDialog();

  const { exportToCsv } = useCsvExport();

  // Swagger dialog state
  const [swaggerDialog, setSwaggerDialog] = useState({
    open: false,
    selectedRole: null,
  });

  // Documentation viewer state
  const [docViewer, setDocViewer] = useState({
    open: false,
    url: '',
    title: '',
  });

  // MCP Endpoint dialog state
  const [mcpDialog, setMcpDialog] = useState({
    open: false,
    selectedRole: null,
  });

  // Copy state
  const [copiedField, setCopiedField] = useState(null);

  const openSwaggerDialog = useCallback(role => {
    setSwaggerDialog({
      open: true,
      selectedRole: role,
    });
  }, []);

  const handleOpenSwaggerForRole = useCallback(() => {
    if (!swaggerDialog.selectedRole?._id) return;

    // Sanitize role name
    const sanitizedRoleName =
      swaggerDialog.selectedRole.name?.replace(/[<>"']/g, '') || 'Unknown Role';

    const apiUrl = getApiUrl();
    const url = `${apiUrl}/api/swagger-ui/openapi/${encodeURIComponent(swaggerDialog.selectedRole._id)}/ui`;

    setDocViewer({
      open: true,
      url,
      title: `Swagger Documentation - ${sanitizedRoleName}`,
    });
    setSwaggerDialog(prev => ({ ...prev, open: false }));
  }, [swaggerDialog.selectedRole]);

  const handleOpenBlueprintsDoc = useCallback(() => {
    const apiUrl = getApiUrl();
    setDocViewer({
      open: true,
      url: `${apiUrl}/api/swagger-ui/blueprints/ui`,
      title: 'Blueprints Documentation',
    });
    setSwaggerDialog(prev => ({ ...prev, open: false }));
  }, []);

  // MCP Endpoint handlers
  const openMcpDialog = useCallback(role => {
    setMcpDialog({
      open: true,
      selectedRole: role,
    });
  }, []);

  const handleCopyToClipboard = useCallback(async (text, fieldName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, []);

  useEffect(() => {
    const abortController = new AbortController();
    fetchRoles();
    return () => abortController.abort();
  }, [fetchRoles]);

  // Sort a copy of the roles (memoized): avoids mutating hook state in place and
  // keeps the data prop reference stable across renders for the memoized DataTable
  const sortedRoles = useMemo(
    () => [...roles].sort((a, b) => a.name.localeCompare(b.name)),
    [roles]
  );

  // Define columns for the modern data table - memoized to prevent re-creation
  const columns = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        sortable: true,
        width: '20%',
        cell: ({ row }) => <div className="font-medium">{row.name}</div>,
      },
      {
        accessorKey: 'description',
        header: 'Description',
        width: '35%',
        cell: ({ row }) => <div className="text-muted-foreground">{row.description || ''}</div>,
      },
      {
        accessorKey: 'isActive',
        header: 'Status',
        type: 'switch',
        width: '15%',
        cell: ({ row, value }) => (
          <div className="flex items-center gap-2">
            <Switch
              checked={value || false}
              onCheckedChange={() => {
                // Prevent multiple rapid clicks
                if (operationInProgress[`toggle-${row._id}`]) {
                  return;
                }
                handleToggleActive(row);
              }}
              aria-label={`Toggle status for ${row.name}`}
              className="data-[state=checked]:bg-ocean-500 data-[state=unchecked]:bg-gray-200"
            />
            <Badge variant={value ? 'active' : 'inactive'} className="text-xs">
              {value ? 'Active' : 'Inactive'}
            </Badge>
          </div>
        ),
      },
      {
        accessorKey: 'mcpEnabled',
        header: 'MCP Server',
        type: 'switch',
        width: '15%',
        cell: ({ row, value }) => (
          <div className="flex items-center gap-2">
            <Switch
              checked={value || false}
              onCheckedChange={() => {
                // Prevent multiple rapid clicks
                if (operationInProgress[`toggle-mcp-${row._id}`]) {
                  return;
                }
                handleToggleMcp(row);
              }}
              aria-label={`Toggle MCP for ${row.name}`}
              className="data-[state=checked]:bg-ocean-500 data-[state=unchecked]:bg-gray-200"
            />
            <Badge variant={value ? 'active' : 'inactive'} className="text-xs">
              {value ? 'Enabled' : 'Disabled'}
            </Badge>
          </div>
        ),
      },
      {
        accessorKey: 'actions',
        header: 'Actions',
        type: 'actions',
        width: '10%',
        actions: [
          {
            label: 'MCP Endpoint',
            icon: Server,
            onClick: role => openMcpDialog(role),
            show: role => role.mcpEnabled, // Only show if MCP is enabled
          },
          {
            label: 'Swagger',
            icon: Info,
            onClick: role => openSwaggerDialog(role),
          },
          {
            label: 'Edit Role',
            icon: Edit,
            onClick: handleEdit,
          },
          {
            label: 'Delete Role',
            icon: Trash2,
            onClick: role => {
              // Prevent multiple rapid clicks
              if (operationInProgress[`delete-${role._id}`]) {
                return;
              }
              openConfirm(role._id, {
                title: 'Delete Role',
                message: 'Are you sure you want to delete this role? This action cannot be undone.',
              });
            },
            destructive: true,
            separator: true,
          },
        ],
      },
    ],
    [
      handleToggleActive,
      handleToggleMcp,
      handleEdit,
      openConfirm,
      operationInProgress,
      openSwaggerDialog,
      openMcpDialog,
    ]
  );

  if (loading) return <LoadingSpinner />;

  return (
    <div className="flex flex-col h-full p-4 sm:p-6 space-y-4 sm:space-y-6">
      {/* Error Messages */}
      {error && (
        <Card className="border-destructive/50 bg-destructive/10">
          <CardContent className="flex items-center gap-2 p-4">
            <AlertCircle className="h-4 w-4 text-destructive" />
            <span className="text-destructive font-medium">{error}</span>
          </CardContent>
        </Card>
      )}

      {/* Header - Responsive */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="text-center sm:text-left">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-ocean-800">Roles</h1>
          <p className="text-muted-foreground text-sm sm:text-base">
            Manage user roles and permissions
          </p>
        </div>
        <div className="flex items-center gap-3 w-full sm:w-auto justify-center sm:justify-end">
          <Button
            variant="ocean"
            size="sm"
            className="flex-1 sm:flex-none bg-ocean-500 text-white hover:bg-ocean-600 border-ocean-500"
            onClick={() => exportToCsv(prepareExportData(), 'roles-list.csv')}
          >
            <Download className="mr-2 h-4 w-4" />
            <span className="hidden sm:inline">Export</span>
          </Button>
          <Button
            size="sm"
            className="flex-1 sm:flex-none bg-ocean-500 text-white hover:bg-ocean-600 border-ocean-500"
            onClick={handleAdd}
          >
            <Plus className="mr-2 h-4 w-4" />
            <span className="sm:hidden">Add</span>
            <span className="hidden sm:inline">Add Role</span>
          </Button>
        </div>
      </div>

      {/* Modern Data Table */}
      <DataTable
        data={sortedRoles}
        columns={columns}
        searchable={true}
        filterable={true}
        exportable={false} // We handle export in header
        loading={loading}
      />

      <ConfirmDialog
        open={confirmState.open}
        title={confirmState.title}
        message={confirmState.message}
        onConfirm={() => handleConfirm(handleDelete)}
        onCancel={closeConfirm}
      />

      {/* Swagger Role Documentation Dialog */}
      <Dialog
        open={swaggerDialog.open}
        onOpenChange={open => setSwaggerDialog(prev => ({ ...prev, open }))}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Role Documentation</DialogTitle>
            <DialogDescription>
              Access documentation for the "{swaggerDialog.selectedRole?.name}" role
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={handleOpenBlueprintsDoc}>
                Open Blueprints Docs
              </Button>
              <Button onClick={handleOpenSwaggerForRole}>Open Role Swagger</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Embedded Documentation Viewer */}
      <Dialog
        open={docViewer.open}
        onOpenChange={open => setDocViewer(prev => ({ ...prev, open }))}
      >
        <DialogContent className="max-w-7xl w-full h-[90vh] p-0">
          <DialogHeader className="p-4 pb-2">
            <DialogTitle>{docViewer.title}</DialogTitle>
            <DialogDescription>
              Interactive API documentation with session-based authentication
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 w-full h-full min-h-[70vh]">
            <SecureIframe
              src={docViewer.url}
              title={docViewer.title}
              className="rounded-b-lg"
              style={{ minHeight: '70vh' }}
              onLoad={() => {
                // Documentation loaded successfully
              }}
              onError={() => {
                console.error('Failed to load documentation');
              }}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* MCP Endpoint Dialog */}
      <Dialog
        open={mcpDialog.open}
        onOpenChange={open => setMcpDialog(prev => ({ ...prev, open }))}
      >
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Server className="h-5 w-5" />
              MCP Server Connection
            </DialogTitle>
            <DialogDescription>
              Connect AI development tools (Claude Desktop, Cursor, etc.) to this role
            </DialogDescription>
          </DialogHeader>

          {mcpDialog.selectedRole && (
            <div className="space-y-6">
              {/* Role Information */}
              <div className="p-4 bg-muted rounded-lg">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="font-medium">Role Name:</div>
                  <div>{mcpDialog.selectedRole.name}</div>
                  <div className="font-medium">Role ID:</div>
                  <div className="font-mono text-xs">{mcpDialog.selectedRole._id}</div>
                  <div className="font-medium">MCP Status:</div>
                  <div>
                    <Badge variant={mcpDialog.selectedRole.mcpEnabled ? 'active' : 'inactive'}>
                      {mcpDialog.selectedRole.mcpEnabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </div>
                </div>
              </div>

              {!mcpDialog.selectedRole.mcpEnabled && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    This role does not have MCP enabled. Edit the role and enable "Enable as MCP
                    Server" to use it with AI tools.
                  </AlertDescription>
                </Alert>
              )}

              {mcpDialog.selectedRole.mcpEnabled && (
                <>
                  {/* API Key Required Section */}
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      <div className="space-y-2">
                        <p className="font-medium">API Key Required for MCP Authentication</p>
                        <p className="text-sm">
                          To connect to this MCP server, you need an API key from an Application:
                        </p>
                        <ol className="text-sm list-decimal list-inside space-y-1 ml-2">
                          <li>
                            Go to <strong>Applications</strong> in the dashboard
                          </li>
                          <li>Create or select an existing application</li>
                          <li>
                            Set <strong>{mcpDialog.selectedRole.name}</strong> as the Default Role
                          </li>
                          <li>
                            Copy the <strong>API Key</strong>
                          </li>
                          <li>Use it in the configuration below</li>
                        </ol>
                      </div>
                    </AlertDescription>
                  </Alert>

                  {/* Claude Desktop Configuration */}
                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      Claude Desktop Configuration
                    </label>
                    <p className="text-sm text-muted-foreground mb-2">
                      Add this to your <code className="text-xs">claude_desktop_config.json</code>{' '}
                      file (replace YOUR_API_KEY with the actual API key):
                    </p>
                    <div className="relative">
                      <pre className="p-4 bg-muted rounded-md text-xs overflow-x-auto">
                        {`{
  "mcpServers": {
    "mirabel-${mcpDialog.selectedRole.name.toLowerCase().replace(/[^a-z0-9]/g, '-')}": {
      "command": "node",
      "args": [
        "${window.location.protocol === 'file:' ? 'C:\\\\path\\\\to\\\\' : ''}mirabel-api\\\\server\\\\mcp\\\\index.js",
        "--api-key",
        "YOUR_API_KEY_HERE"
      ]
    }
  }
}`}
                      </pre>
                      <Button
                        variant="outline"
                        size="sm"
                        className="absolute top-2 right-2"
                        onClick={() =>
                          handleCopyToClipboard(
                            `{\n  "mcpServers": {\n    "mirabel-${mcpDialog.selectedRole.name.toLowerCase().replace(/[^a-z0-9]/g, '-')}": {\n      "command": "node",\n      "args": [\n        "C:\\\\path\\\\to\\\\mirabel-api\\\\server\\\\mcp\\\\index.js",\n        "--api-key",\n        "YOUR_API_KEY_HERE"\n      ]\n    }\n  }\n}`,
                            'config'
                          )
                        }
                      >
                        {copiedField === 'config' ? (
                          <>✓ Copied</>
                        ) : (
                          <>
                            <Copy className="h-4 w-4 mr-1" />
                            Copy
                          </>
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Config File Locations */}
                  <div>
                    <label className="text-sm font-medium mb-2 block">Config File Locations</label>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-start gap-2">
                        <span className="font-medium min-w-[80px]">Windows:</span>
                        <code className="text-xs bg-muted px-2 py-1 rounded">
                          %APPDATA%\Claude\claude_desktop_config.json
                        </code>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="font-medium min-w-[80px]">macOS:</span>
                        <code className="text-xs bg-muted px-2 py-1 rounded">
                          ~/Library/Application Support/Claude/claude_desktop_config.json
                        </code>
                      </div>
                    </div>
                  </div>

                  {/* Quick Instructions */}
                  <div className="p-4 border rounded-lg">
                    <h4 className="font-medium mb-2 text-sm">Quick Setup Steps:</h4>
                    <ol className="text-sm space-y-1 list-decimal list-inside text-muted-foreground">
                      <li>Go to Applications and get an API key (set this role as Default Role)</li>
                      <li>Copy the configuration above</li>
                      <li>Open your Claude Desktop config file</li>
                      <li>
                        Paste the configuration and replace YOUR_API_KEY_HERE with your actual API
                        key
                      </li>
                      <li>Update the path to your mirabel-api installation</li>
                      <li>Restart Claude Desktop</li>
                      <li>Look for the 🔌 icon to confirm connection</li>
                    </ol>
                  </div>

                  {/* Documentation Link */}
                  <div className="text-sm text-muted-foreground">
                    For detailed setup instructions, see{' '}
                    <code className="text-xs">docs/MCP_SERVER_GUIDE.md</code>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RoleList;
